import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Target, Copy, Calculator } from 'lucide-react';
import { TPSLCalculatorService } from '@/services/TPSLCalculatorService';
import { toast } from 'sonner';

interface TargetResults {
  symbol: string;
  currentPrice: number;
  requiredTpPercent: number;
  requiredSlPercent: number;
  tpPrice: number;
  slPrice: number;
  riskRewardRatio: number;
  tvPositionValue: number;
  priceSource: 'cache' | 'api';
}

export function TargetModeCalculator() {
  const [symbol, setSymbol] = useState('EURUSD');
  const [targetTpUsd, setTargetTpUsd] = useState('200');
  const [targetSlUsd, setTargetSlUsd] = useState('100');
  const [tvPositionSize, setTvPositionSize] = useState('10000');
  const [results, setResults] = useState<TargetResults | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const calculateTarget = async () => {
    if (!symbol || !targetTpUsd || !targetSlUsd || !tvPositionSize) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsCalculating(true);
    try {
      // First get the current price
      const priceResult = await TPSLCalculatorService.getCurrentPrice(symbol.toUpperCase());
      const currentPrice = priceResult.price;
      const positionValue = parseFloat(tvPositionSize) * currentPrice;

      // Calculate required percentages
      const requiredTpPercent = (parseFloat(targetTpUsd) / positionValue) * 100;
      const requiredSlPercent = (parseFloat(targetSlUsd) / positionValue) * 100;

      // Calculate corresponding prices
      const tpPrice = currentPrice * (1 + requiredTpPercent / 100);
      const slPrice = currentPrice * (1 - requiredSlPercent / 100);

      // Risk:Reward ratio
      const riskRewardRatio = parseFloat(targetTpUsd) / parseFloat(targetSlUsd);

      setResults({
        symbol: symbol.toUpperCase(),
        currentPrice,
        requiredTpPercent,
        requiredSlPercent,
        tpPrice,
        slPrice,
        riskRewardRatio,
        tvPositionValue: positionValue,
        priceSource: priceResult.source
      });

      toast.success('Target calculation completed');
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Failed to calculate target parameters');
    } finally {
      setIsCalculating(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Target Parameters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="symbol">Trading Symbol</Label>
            <Input
              id="symbol"
              placeholder="EURUSD, BTCUSD, XAUUSD..."
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="targetTpUsd">Target Profit ($)</Label>
              <Input
                id="targetTpUsd"
                type="number"
                step="0.01"
                placeholder="200"
                value={targetTpUsd}
                onChange={(e) => setTargetTpUsd(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="targetSlUsd">Target Loss ($)</Label>
              <Input
                id="targetSlUsd"
                type="number"
                step="0.01"
                placeholder="100"
                value={targetSlUsd}
                onChange={(e) => setTargetSlUsd(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tvPositionSize">TradingView Position Size</Label>
            <Input
              id="tvPositionSize"
              type="number"
              placeholder="10000"
              value={tvPositionSize}
              onChange={(e) => setTvPositionSize(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Number of units/shares/contracts in TradingView
            </p>
          </div>

          <Button 
            onClick={calculateTarget} 
            disabled={isCalculating}
            className="w-full"
          >
            {isCalculating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="h-4 w-4 mr-2" />
                Calculate Required %
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Required Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {results ? (
            <div className="space-y-4">
              {/* Symbol & Price Info */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <div className="font-medium">{results.symbol}</div>
                  <div className="text-sm text-muted-foreground">
                    Current: ${results.currentPrice.toFixed(5)}
                    <Badge variant="outline" className="ml-2 text-xs">
                      {results.priceSource === 'cache' ? 'Cached' : 'Live'}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(results.currentPrice.toString(), 'Current price')}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>

              {/* Required Percentages - Main Result */}
              <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="text-sm text-muted-foreground mb-2">Required TradingView Settings</div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground">TP %</div>
                      <div className="font-mono text-lg font-bold text-green-600">
                        {results.requiredTpPercent.toFixed(3)}%
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(results.requiredTpPercent.toFixed(3), 'TP %')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground">SL %</div>
                      <div className="font-mono text-lg font-bold text-red-600">
                        {results.requiredSlPercent.toFixed(3)}%
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(results.requiredSlPercent.toFixed(3), 'SL %')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Price Levels */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">TP Price</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(results.tpPrice.toFixed(5), 'TP Price')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="font-medium">${results.tpPrice.toFixed(5)}</div>
                </div>

                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">SL Price</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(results.slPrice.toFixed(5), 'SL Price')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="font-medium">${results.slPrice.toFixed(5)}</div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div>
                    <div className="text-sm text-muted-foreground">Risk:Reward Ratio</div>
                    <div className="font-medium">1:{results.riskRewardRatio.toFixed(2)}</div>
                  </div>
                  <Badge variant={results.riskRewardRatio >= 2 ? "default" : "secondary"}>
                    {results.riskRewardRatio >= 2 ? "Good R:R" : "Low R:R"}
                  </Badge>
                </div>

                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="text-sm text-muted-foreground">TV Position Value</div>
                  <div className="font-medium">${results.tvPositionValue.toLocaleString()}</div>
                </div>

                {/* TradingView Copy Instructions */}
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="text-sm font-medium text-blue-600 mb-1">TradingView Setup</div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>• Set Take Profit: {results.requiredTpPercent.toFixed(3)}%</div>
                    <div>• Set Stop Loss: {results.requiredSlPercent.toFixed(3)}%</div>
                    <div>• Position Size: {tvPositionSize} units</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              Enter your target profit/loss amounts to calculate required percentages
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}